import { Check, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function Pricing() {
    const { t } = useTranslation();

    const plans = [
        {
            name: t("pricing.starter_name"),
            price: "$99",
            period: t("pricing.per_month"),
            users: t("pricing.starter_users"),
            features: [
                t("pricing.unlimited_exams"),
                t("pricing.offline_first"),
                t("pricing.photo_validation"),
                t("pricing.basic_analytics"),
            ],
            cta: t("pricing.book_demo"),
        },
        {
            name: t("pricing.pro_name"),
            price: "$299",
            period: t("pricing.per_month"),
            users: t("pricing.pro_users"),
            features: [
                t("pricing.unlimited_exams"),
                t("pricing.offline_first"),
                t("pricing.photo_validation"),
                t("pricing.fingerprint_validation"),
                t("pricing.branded_certificates"),
                t("pricing.api_access"),
                t("pricing.admin_accounts_3"),
            ],
            cta: t("pricing.book_demo"),
            highlighted: true,
        },
        {
            name: t("pricing.business_name"),
            price: "$799",
            period: t("pricing.per_month"),
            users: t("pricing.business_users"),
            features: [
                t("pricing.everything_in_pro"),
                t("pricing.sub_portals"),
                t("pricing.mfa"),
                t("pricing.ip_restriction"),
                t("pricing.priority_support"),
                t("pricing.unlimited_admins"),
            ],
            cta: t("pricing.book_demo"),
        },
        {
            name: t("pricing.enterprise_name"),
            price: t("pricing.custom"),
            period: "",
            users: t("pricing.enterprise_users"),
            features: [
                t("pricing.everything_in_business"),
                t("pricing.fdle_fbi_integration"),
                t("pricing.custom_reporting"),
                t("pricing.account_manager"),
                t("pricing.enhanced_sla"),
            ],
            cta: t("pricing.contact_sales"),
        },
    ];

    return (
        <main className="bg-gradient-to-b from-indigo-900 via-indigo-950 to-black text-gray-100 min-h-screen">
            {/* 🔹 Hero */}
            <section className="py-20 text-center">
                <h1 className="text-4xl font-extrabold text-white">
                    {t("pricing.title")}
                </h1>
                <p className="mt-4 text-lg text-indigo-200 max-w-2xl mx-auto">
                    {t("pricing.subtitle")}
                </p>
            </section>

            {/* 🔹 Pricing Cards */}
            <section className="pb-20 max-w-6xl mx-auto px-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {plans.map((plan, idx) => (
                    <div
                        key={idx}
                        className={`p-6 rounded-2xl border flex flex-col shadow-lg transition ${plan.highlighted
                                ? "bg-indigo-800/40 border-indigo-400 shadow-indigo-600/20"
                                : "bg-slate-900/50 border-slate-800 hover:border-indigo-600/40"
                            }`}
                    >
                        <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
                        <p className="mt-2 text-3xl font-bold text-white">
                            {plan.price}
                            <span className="text-base font-normal text-gray-400 ml-1">
                                {plan.period}
                            </span>
                        </p>
                        <p className="mt-1 text-sm text-gray-400">{plan.users}</p>
                        <ul className="mt-6 space-y-2 flex-1">
                            {plan.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                                    <Check className="w-4 h-4 text-green-400 mt-0.5" /> {f}
                                </li>
                            ))}
                        </ul>
                        <Link
                            to={
                                plan.cta === t("pricing.contact_sales")
                                    ? "/contact"
                                    : "/solutions/examspro/support"
                            }
                            className={`mt-6 block text-center px-4 py-2 rounded-lg font-medium transition ${plan.highlighted
                                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                    : "bg-slate-800 text-gray-200 hover:bg-slate-700"
                                }`}
                        >
                            {plan.cta}
                        </Link>
                    </div>
                ))}
            </section>

            {/* 🔹 Feature Comparison */}
            <section className="py-16 bg-slate-950 border-t border-slate-800">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-2xl font-bold text-white text-center">
                        {t("pricing.feature_comparison")}
                    </h2>
                    <div className="overflow-x-auto mt-8">
                        <table className="min-w-full border-collapse text-sm text-gray-300">
                            <thead>
                                <tr className="bg-slate-900/60">
                                    <th className="border border-slate-700 px-4 py-3 text-left text-gray-200">
                                        {t("pricing.features")}
                                    </th>
                                    {plans.map((p, i) => (
                                        <th
                                            key={i}
                                            className="border border-slate-700 px-4 py-3 text-center text-gray-200"
                                        >
                                            {p.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { feature: t("pricing.offline_first"), values: [true, true, true, true] },
                                    { feature: t("pricing.photo_validation"), values: [true, true, true, true] },
                                    { feature: t("pricing.fingerprint_validation"), values: [false, true, true, true] },
                                    { feature: t("pricing.api_access"), values: [false, true, true, true] },
                                    { feature: t("pricing.sub_portals"), values: [false, false, true, true] },
                                    { feature: t("pricing.fdle_fbi_integration"), values: [false, false, false, true] },
                                ].map((row, i) => (
                                    <tr
                                        key={i}
                                        className={i % 2 === 0 ? "bg-slate-900/40" : "bg-slate-900/20"}
                                    >
                                        <td className="border border-slate-800 px-4 py-2 text-gray-200">
                                            {row.feature}
                                        </td>
                                        {row.values.map((v, j) => (
                                            <td
                                                key={j}
                                                className="border border-slate-800 px-4 py-2 text-center"
                                            >
                                                {v ? (
                                                    <Check className="w-4 h-4 text-green-400 mx-auto" />
                                                ) : (
                                                    <Minus className="w-4 h-4 text-gray-500 mx-auto" />
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* 🔹 CTA Footer */}
            <section className="py-20 bg-indigo-900/60 text-center border-t border-indigo-800">
                <h2 className="text-2xl font-bold text-white">
                    {t("pricing.cta_title")}
                </h2>
                <p className="mt-3 text-indigo-200">{t("pricing.cta_text")}</p>
                <div className="mt-6 flex gap-4 justify-center">
                    <Link
                        to="/solutions/examspro/support"
                        className="bg-indigo-600 text-white px-6 py-3 rounded-lg shadow hover:bg-indigo-700 transition"
                    >
                        {t("pricing.book_demo")}
                    </Link>
                </div>
            </section>
        </main>
    );
}
